/**
 * Feature Flags
 *
 * Centralized feature flag management for controlling experimental features
 * and gradual rollouts.
 *
 * NOTE for Agents: When adding a feature flag to the enum, you'll also need to
 * add a key value pair in ExperimentalFeatures.tsx to list it in the menu.
 */
import { useEffect, useState } from 'react';

const FEATURE_FLAGS_CHANGED_EVENT = 'museflow_feature_flags_changed';

/**
 * Feature flag keys
 */
export enum FeatureFlag {
  /**
   * Layered 2.5D roadmap with repertoire stages and live 3D models. Off by
   * default and forced off in production; outside production, opt in with
   * `?USE_2_5D_ROADMAP=true` or Experimental Features.
   */
  USE_2_5D_ROADMAP = 'USE_2_5D_ROADMAP',
  USE_ERROR_FEEDBACK = 'USE_ERROR_FEEDBACK',
  USE_AUDIO_RECOGNITION = 'USE_AUDIO_RECOGNITION',
  USE_ANNOUNCEMENT_MODAL = 'USE_ANNOUNCEMENT_MODAL',
  USE_REPERTOIRE_CREATE = 'USE_REPERTOIRE_CREATE',
  USE_REPERTOIRE_LIBRARY = 'USE_REPERTOIRE_LIBRARY',
  USE_REPERTOIRE_PLAYBACK_MODES = 'USE_REPERTOIRE_PLAYBACK_MODES',
  /**
   * Repertoire browse filtering. When enabled, the Browse page shows the
   * filter menu (genre/range-slider UI + live result counts) next to the
   * search bar and applied filters narrow the grid. When disabled, the menu
   * is hidden and Browse renders exactly as it did pre-feature.
   */
  USE_REPERTOIRE_FILTERING = 'USE_REPERTOIRE_FILTERING',
  USE_HISTORY_OF_PIANO_MUSIC = 'USE_HISTORY_OF_PIANO_MUSIC',
  /**
   * Private weekly circles, community challenges, and milestone celebrations.
   * Opt in through Experimental Features in development. The sample-data preview
   * stays off by default and is forced off in production until backend APIs exist.
   */
  USE_COMMUNITY_HUB = 'USE_COMMUNITY_HUB',
  USE_EXERCISES = 'USE_EXERCISES',
  /**
   * Debug Dummy MIDI. Experimental developer aid. When enabled, the computer
   * keyboard is patched to emit MIDI note on/off events so the app can be
   * exercised without a physical MIDI keyboard (home row = white keys starting
   * A→E3, top row = black keys). Also surfaces a floating keyboard button on the
   * Roadmap that opens a piano calibration/demo scene. Off (and forced off in
   * production) by default.
   */
  DEBUG_DUMMY_MIDI = 'DEBUG_DUMMY_MIDI',
  /**
   * Live finger numbers in sight reading. When enabled, each phrase is fingered
   * in the app as it is queued (and again whenever the transposition changes),
   * those numbers replace any carried by the phrase file, and the lesson
   * settings gain a finger-number density control. Off (and forced off in
   * production) by default.
   */
  USE_ERGOBOT_FINGERING = 'USE_ERGOBOT_FINGERING',
  /**
   * Fingering timing readout. A testing aid for live finger numbers: with
   * `USE_ERGOBOT_FINGERING` on as well, the lesson shows how long fingering
   * each phrase took (`?ERGOBOT_TIMING=1`). Off (and forced off in production)
   * by default; it does nothing without live finger numbers.
   */
  ERGOBOT_TIMING = 'ERGOBOT_TIMING',
  /**
   * Plays as if the account said "a piano or keyboard without MIDI"
   * (`play_input = unconnected_piano`) on this device, so play is ungraded (see
   * Utils/playAlong/effectiveGrading). A development aid until the app asks
   * what the player plays on. Off by default and forced off in production.
   */
  DEV_PLAY_INPUT_UNCONNECTED_PIANO = 'DEV_PLAY_INPUT_UNCONNECTED_PIANO',
}

/**
 * Coerce an env-sourced string into a boolean, returning the fallback when the
 * var is absent.
 *
 * Takes the ALREADY-INLINED value, not a key. Each caller must reference its env
 * var with a direct static `process.env.REACT_APP_X` access so Vite's envPlugin
 * (`vite.config.ts`) inlines it at build time via `define` (literal text
 * substitution). The previous dynamic `process.env[key]` form was NOT inlined by
 * Vite - the bracket lookup is left untouched by `define`, so it read `undefined`
 * in the browser bundle and every flag silently fell back to its default. Jest
 * (real `process.env` in Node) hid the bug. See MUS-509.
 *
 * @param value  the inlined `process.env.REACT_APP_X` value (`string | undefined`)
 * @param fallback returned when the var is unset or empty
 */
export function envBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined || value === '') return fallback;
  return value === 'true' || value === '1';
}

/**
 * Default values for feature flags
 */
const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  // Each entry passes a direct static `process.env.REACT_APP_X` access so Vite
  // inlines the value at build time (see envBoolean / MUS-509). Do NOT reintroduce
  // a dynamic `process.env[key]` form - Vite will not inline it.
  [FeatureFlag.USE_2_5D_ROADMAP]: envBoolean(process.env.REACT_APP_USE_2_5D_ROADMAP, false),
  [FeatureFlag.USE_ERROR_FEEDBACK]: false,
  [FeatureFlag.USE_AUDIO_RECOGNITION]: envBoolean(
    process.env.REACT_APP_USE_AUDIO_RECOGNITION,
    false,
  ),
  [FeatureFlag.USE_ANNOUNCEMENT_MODAL]: envBoolean(
    process.env.REACT_APP_USE_ANNOUNCEMENT_MODAL,
    false,
  ),
  [FeatureFlag.USE_REPERTOIRE_CREATE]: envBoolean(
    process.env.REACT_APP_USE_REPERTOIRE_CREATE,
    false,
  ),
  [FeatureFlag.USE_REPERTOIRE_LIBRARY]: envBoolean(
    process.env.REACT_APP_USE_REPERTOIRE_LIBRARY,
    false,
  ),
  [FeatureFlag.USE_REPERTOIRE_PLAYBACK_MODES]: envBoolean(
    process.env.REACT_APP_USE_REPERTOIRE_PLAYBACK_MODES,
    false,
  ),
  [FeatureFlag.USE_REPERTOIRE_FILTERING]: envBoolean(
    process.env.REACT_APP_USE_REPERTOIRE_FILTERING,
    false,
  ),
  [FeatureFlag.USE_HISTORY_OF_PIANO_MUSIC]: envBoolean(
    process.env.REACT_APP_USE_HISTORY_OF_PIANO_MUSIC,
    false,
  ),
  [FeatureFlag.USE_COMMUNITY_HUB]: envBoolean(process.env.REACT_APP_USE_COMMUNITY_HUB, false),
  [FeatureFlag.USE_EXERCISES]: envBoolean(process.env.REACT_APP_USE_EXERCISES, false),
  [FeatureFlag.DEBUG_DUMMY_MIDI]: envBoolean(process.env.REACT_APP_DEBUG_DUMMY_MIDI, false),
  [FeatureFlag.USE_ERGOBOT_FINGERING]: envBoolean(
    process.env.REACT_APP_USE_ERGOBOT_FINGERING,
    false,
  ),
  [FeatureFlag.ERGOBOT_TIMING]: false,
  [FeatureFlag.DEV_PLAY_INPUT_UNCONNECTED_PIANO]: false,
};

const PRODUCTION_FORCED_OFF_FLAGS = new Set<FeatureFlag>([
  FeatureFlag.USE_AUDIO_RECOGNITION,
  FeatureFlag.USE_REPERTOIRE_CREATE,
  FeatureFlag.USE_REPERTOIRE_LIBRARY,
  FeatureFlag.DEBUG_DUMMY_MIDI,
  FeatureFlag.USE_EXERCISES,
  FeatureFlag.USE_COMMUNITY_HUB,
  FeatureFlag.USE_2_5D_ROADMAP,
  FeatureFlag.USE_ERGOBOT_FINGERING,
  FeatureFlag.ERGOBOT_TIMING,
  FeatureFlag.DEV_PLAY_INPUT_UNCONNECTED_PIANO,
]);

function isProductionBuild(): boolean {
  return process.env.REACT_APP_NODE_ENV === 'prd';
}

function isForcedOffInProduction(flag: FeatureFlag): boolean {
  return isProductionBuild() && PRODUCTION_FORCED_OFF_FLAGS.has(flag);
}

/**
 * Local storage key for feature flags
 */
const STORAGE_KEY = 'museflow_feature_flags';

/**
 * Get all feature flags from local storage
 */
function getStoredFlags(): Partial<Record<FeatureFlag, boolean>> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to read feature flags from local storage:', error);
  }
  return {};
}

/**
 * Save feature flags to local storage
 */
function saveFlags(flags: Partial<Record<FeatureFlag, boolean>>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch (error) {
    console.warn('Failed to save feature flags to local storage:', error);
  }
}

function notifyFeatureFlagsChanged(): void {
  window.dispatchEvent(new Event(FEATURE_FLAGS_CHANGED_EVENT));
}

/**
 * Check if a feature flag is enabled
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  if (isForcedOffInProduction(flag)) {
    return false;
  }

  // Check URL parameter first (for testing)
  const urlParams = new URLSearchParams(window.location.search);
  const urlValue = urlParams.get(flag);
  if (urlValue !== null) {
    return urlValue === 'true' || urlValue === '1';
  }

  // Check local storage
  const storedFlags = getStoredFlags();
  if (flag in storedFlags) {
    return storedFlags[flag]!;
  }

  // Return default value
  return DEFAULT_FLAGS[flag];
}

/**
 * Set a feature flag value
 */
export function setFeatureFlag(flag: FeatureFlag, enabled: boolean): void {
  if (isForcedOffInProduction(flag)) {
    return;
  }

  const storedFlags = getStoredFlags();
  storedFlags[flag] = enabled;
  saveFlags(storedFlags);
  notifyFeatureFlagsChanged();
}

/**
 * Reset a feature flag to its default value
 */
export function resetFeatureFlag(flag: FeatureFlag): void {
  const storedFlags = getStoredFlags();
  delete storedFlags[flag];
  saveFlags(storedFlags);
  notifyFeatureFlagsChanged();
}

/**
 * Reset all feature flags to their default values
 */
export function resetAllFeatureFlags(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    notifyFeatureFlagsChanged();
  } catch (error) {
    console.warn('Failed to reset feature flags:', error);
  }
}

/**
 * Get all current feature flag values
 */
export function getAllFeatureFlags(): Record<FeatureFlag, boolean> {
  const result: Record<FeatureFlag, boolean> = { ...DEFAULT_FLAGS };
  const storedFlags = getStoredFlags();

  for (const flag of Object.values(FeatureFlag)) {
    if (isForcedOffInProduction(flag)) {
      result[flag] = false;
    } else if (flag in storedFlags) {
      result[flag] = storedFlags[flag]!;
    }
  }

  return result;
}

/**
 * Hook for using feature flags in React components
 * Note: This is a simple implementation. For real-time updates,
 * consider using a context provider or state management solution.
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flag));

  useEffect(() => {
    const updateEnabled = () => setEnabled(isFeatureEnabled(flag));

    window.addEventListener(FEATURE_FLAGS_CHANGED_EVENT, updateEnabled);
    window.addEventListener('storage', updateEnabled);

    return () => {
      window.removeEventListener(FEATURE_FLAGS_CHANGED_EVENT, updateEnabled);
      window.removeEventListener('storage', updateEnabled);
    };
  }, [flag]);

  return enabled;
}
